import FilteredNewsCards from '../../components/organisms/filtered-news-cards/filtered-news-cards';
import { generateDatesASC } from '../../utils/date';
import { PaginationModel } from '../../interface/pagination';
import Pagination from '../../components/atoms/pagination/pagination';
import { usePagination } from '../../hooks/usePagination';
import { useNewsAndEventsFilter } from '../../hooks/useNewsAndEventsFilter';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import ArchiveDates from '../../components/molecules/archive-dates/archive-dates';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { MetaPages } from '../../enums/pages';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import SectionTitle from '../../components/atoms/section-title/section-title';
import {
  getNewsAndEventsContent,
  getNewsBannerContent
} from '../../services/news';
import Tabs from '../../components/molecules/tabs/tabs';
import HeaderContainer from '../../components/molecules/header-container/header-container';
import { newsRoutes } from '../../config/routes/pages';
import { ContentType } from '../../types/content';
import {
  NewsAndEventsHeaderEnums,
  NewsAndEventsFilterEnums
} from '../../enums/news';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import NewsCard from '../../components/molecules/news-card/news-card';
import {
  API_ASSETS_ROUTE,
  strapiAssetBaseUrl
} from '../../config/routes/assets';

type IndexProps = {
  newsBannerContent: ContentType;
  newsAndEventsContent: NewsAndEventsModel[];
  pagination: PaginationModel;
};

export function Index(props: IndexProps) {
  const { newsBannerContent, newsAndEventsContent, pagination } = props;
  const { EVENTS: EVENTS_HEADER } = NewsAndEventsHeaderEnums;
  const { EVENTS: EVENTS_FILTER } = NewsAndEventsFilterEnums;

  const yearsPublished = generateDatesASC(newsAndEventsContent);

  const { filteredNewsAndEventsContent, selectedYear, handleClickYear } =
    useNewsAndEventsFilter(newsAndEventsContent, EVENTS_FILTER);

  const { currentContent, currentPage, handlePageClick } = usePagination(
    EVENTS_FILTER,
    filteredNewsAndEventsContent
  );

  const gratefulGatherings = (
    <NewsCard
      ctaLink="21524"
      description="Create memories worth celebrating with a Thanksgiving spread that
        harmoniously blends your favorite holiday flavors, incorporating the
        exceptional quality of Sub-Zero and Wolf bringing a feast in every bite."
      key="21524"
      src={`${strapiAssetBaseUrl}/1_4f53aaf4f1.png`}
      title="Grateful Gatherings"
    />
  );

  const summerAtHamptons = (
    <NewsCard
      ctaLink="26801"
      description="Experience the best of luxe summer living with Sub-Zero and Wolf's first cooking demo for the year."
      key="26801"
      src={`${strapiAssetBaseUrl}/1_8be5194c8b.jpg`}
      title="Summer in the Hamptons"
    />
  );

  const freshnessMedley = (
    <NewsCard
      ctaLink="072923"
      description="Embark on a culinary adventure made with fresh ingredients as Sub-Zero and Wolf partners with Good Food Community for a ‘Freshness Medley’"
      key="072923"
      src={`${strapiAssetBaseUrl}/1_85f6a5925e.jpg`}
      title="Indulge in a Fresh Perspective"
    />
  );

  const filteredNewsCards = (
    <>
      {gratefulGatherings}
      {freshnessMedley}
      {summerAtHamptons}
      <FilteredNewsCards
        currentContent={currentContent}
        filteredNewsAndEventsContent={filteredNewsAndEventsContent}
        selectedYear={selectedYear}
      />
    </>
  );

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <HeaderContainer {...newsBannerContent} />
      <Tabs tabItems={newsRoutes} />
      <MarginSpacing margin="mb-10 lg:mb-20" />
      <SectionTitle title={EVENTS_HEADER} />
      <MarginSpacing margin="mb-10 lg:mb-20" />
      <div className="px-10">
        <ContainerWithSidebar
          featuredContents={
            <ArchiveDates
              dates={yearsPublished}
              selectedYear={selectedYear}
              onClickSelectYear={handleClickYear}
            />
          }
        >
          <div className="space-y-8 mb-5 lg:mb-14">{filteredNewsCards}</div>
          <Pagination
            content={filteredNewsAndEventsContent}
            currentPage={currentPage}
            pageCount={pagination.pageCount}
            pageLimit={pagination.pageSize}
            onPageClick={handlePageClick}
          />
        </ContainerWithSidebar>
      </div>
    </>
  );
}

export async function getStaticProps() {
  const newsBannerContent = await getNewsBannerContent();
  const newsAndEventsContent = await getNewsAndEventsContent(
    NewsAndEventsFilterEnums.EVENTS
  );
  const { pagination } = newsAndEventsContent.meta;

  return {
    props: {
      newsBannerContent,
      newsAndEventsContent: newsAndEventsContent.data,
      pagination
    }
  };
}

export default Index;
